// Top-level app: router + HubSpot context provider.
//
// W1B reconciliation (2026-10-19): the root route now resolves to a
// server-selected next task instead of the legacy /my-work placeholder.
// /manager redirects to the approved Team Live screen under a role gate,
// and Team Live / Executive Live / Coaching surfaces are wrapped in
// RequireRole so a sales_rep who navigates directly sees "Not available
// for your role" instead of a sampled page. Legacy /my-work bookmarks
// still work: they redirect to the root entry.
import { useEffect, useState, type ReactNode } from 'react';
import {
  BrowserRouter,
  MemoryRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from 'react-router-dom';

import { HubSpotContextProvider, useHubSpotContext } from './auth/hubspotContext.js';
import { SessionProvider } from './auth/session-context.js';
import { ContactLayout, Layout } from './shell/Layout.js';
import { RootErrorBoundary } from './shell/RootErrorBoundary.js';
import { CurrentAction } from './tabs/CurrentAction.js';
import { BuildOrder } from './tabs/BuildOrder.js';
import { Horses } from './tabs/Horses.js';
import { Subscription } from './tabs/Subscription.js';
import { Gifts } from './tabs/Gifts.js';
import { ColicCoverage } from './tabs/ColicCoverage.js';
import { Relationships } from './tabs/Relationships.js';
import { ServiceNeeds } from './tabs/ServiceNeeds.js';
import { ReviewStateProvider } from './screens/_shared/ReviewState.js';
import { CustomerReviewBoundary } from './screens/_shared/CustomerReviewBoundary.js';
import { WorkspaceChrome } from './screens/_shared/WorkspaceChrome.js';
import { RequireRole } from './screens/_shared/RequireRole.js';
import { HistoryReviewScreen } from './screens/HistoryReview/index.js';
import { LettersReviewScreen } from './screens/LettersReview/index.js';
import {
  CoachingStub,
  MyCreditStub,
} from './tabs/SidebarStubs.js';

// Design-approved screen ports (Codex pass, 2026-10-05).
import { CurrentActionReviewScreen } from './screens/CurrentActionReview/index.js';
import { MyActivityReviewScreen } from './screens/MyActivityReview/index.js';
import { BuildOrderReviewScreen } from './screens/BuildOrderReview/index.js';
import { PaymentReviewScreen } from './screens/PaymentReview/index.js';
import { SubscriptionsReviewScreen } from './screens/SubscriptionsReview/index.js';
import { ColicCoverageReviewScreen } from './screens/ColicCoverageReview/index.js';
import { GiftsReviewScreen } from './screens/GiftsReview/index.js';
import { HorsesReviewScreen } from './screens/HorsesReview/index.js';
import { RelationshipsReviewScreen } from './screens/RelationshipsReview/index.js';

// R8: full React ports replacing R7 stubs, one screen per commit.
import { TeamLiveScreen } from './screens/TeamLive/index.js';
import { ExecutiveLiveScreen } from './screens/ExecutiveLive/index.js';
import { LiveChatScreen } from './screens/LiveChat/index.js';
import { CustomerAddressesScreen } from './screens/CustomerAddresses/index.js';
import { EditPreferencesScreen } from './screens/EditPreferences/index.js';
import { CallsCallbacksScreen } from './screens/CallsCallbacks/index.js';
import { SpecialFollowupsScreen } from './screens/SpecialFollowups/index.js';
import { NewCustomerIntakeScreen } from './screens/NewCustomerIntake/index.js';
import { IncomingEmailScreen } from './screens/IncomingEmail/index.js';
import { IncomingTextScreen } from './screens/IncomingText/index.js';


export interface AppProps {
  // Test-only overrides. Production uses the browser router and the real
  // postMessage handshake.
  routerOverride?: 'memory';
  initialEntries?: string[];
  forceMockContext?: boolean;
}

export function App(props: AppProps = {}): ReactNode {
  const { routerOverride, initialEntries, forceMockContext } = props;
  // W3C reconciliation (2026-10-20): SessionProvider wraps the router at
  // the app root. The provider runs the HubSpot iframe handshake (or
  // falls back to the VITE_DEV_MOCK_CONTEXT dev-mock loader via
  // isDevMockSession when the parent is not a HubSpot host). Without
  // this mount, screens that call useSession() stayed in preview mode
  // even when the parent window shipped a real session; W1B left the
  // mount off and W2A upper + lower picked up the regression. The
  // forceMockContext test prop forwards into forceMock on both
  // providers so the memory-router tests still render the fixture.
  const providerProps = forceMockContext ? { forceMock: true } : {};
  const routes = (
    <SessionProvider {...providerProps}>
      <HubSpotContextProvider {...providerProps}>
        <AppRoutes />
      </HubSpotContextProvider>
    </SessionProvider>
  );

  // W22 (2026-10-20): wrap the full tree so a synchronous throw inside
  // SessionProvider/HubSpotContextProvider renders a visible error page
  // instead of a blank iframe. The pre-W22 build had no top-level
  // boundary; any provider throw bailed the entire mount silently.
  const router = routerOverride === 'memory' ? (
    <MemoryRouter initialEntries={initialEntries ?? ['/']}>{routes}</MemoryRouter>
  ) : (
    <BrowserRouter>{routes}</BrowserRouter>
  );
  return <RootErrorBoundary>{router}</RootErrorBoundary>;
}

function AppRoutes(): ReactNode {
  const context = useHubSpotContext();
  // Wait for the existing context provider before mounting editable drafts.
  // This avoids losing an unresolved workspace's edits when its scope settles;
  // it does not replace the runtime's authenticated session verification.
  //
  // W22 (2026-10-20): the pending screen now exposes the handshake state
  // so a stuck "Opening..." is distinguishable from a blank mount. If
  // the parent frame never ships a hubspot.session message the
  // URL-bootstrap grace timer (hubspot-handshake.ts) flips this to
  // 'ready' with a placeholder session so the UI mounts something.
  //
  // W23 (2026-10-20): the pending screen now also counts elapsed seconds
  // and surfaces the handshake state live. The URL-bootstrap backup
  // timer MUST fire by 3s; if the user still sees pending after that,
  // Doug knows the problem is deeper than a throttled setTimeout
  // (which is useful signal) and can click a manual bootstrap button
  // that posts location.reload() so the handshake runs again.
  if (context.status === 'pending') {
    return <PendingScreen />;
  }
  if (context.status === 'error') return <main style={{ padding: 24 }}>
    <h1>Sales Workspace unavailable</h1><p role="alert">{context.error.message}</p>
  </main>;
  const scopeKey = String(context.context.portalId) + ':' + context.context.userId;
  return (
    <ReviewStateProvider scopeKey={scopeKey}>
    <Routes>
      {/* Root entry: route to the server-selected next task. The landing
       * reads the session's current objectId and redirects into the
       * approved Current Action review for that customer. */}
      <Route index element={<NextTaskLanding />} />
      {/* Legacy /my-work bookmarks resolve to the root entry so previously
       * saved returnTo state continues to work. */}
      <Route path="/my-work" element={<Navigate to="/" replace />} />
      {/* Design-approved full-chrome screens. Each renders its own
       * host-space, topbar, rail and footer, so they live outside the
       * placeholder <Layout /> shell. */}
      <Route path="/my-activity" element={<MyActivityReviewScreen />} />
      <Route element={<CustomerReviewBoundary />}>
      <Route
        path="/current-action/:customerId"
        element={<CurrentActionReviewScreen />}
      />
      <Route
        path="/build-order/:customerId"
        element={<BuildOrderReviewScreen />}
      />
      <Route path="/payment/:customerId" element={<PaymentReviewScreen />} />
      <Route
        path="/subscriptions/:customerId"
        element={<SubscriptionsReviewScreen />}
      />
      <Route
        path="/colic-coverage/:customerId"
        element={<ColicCoverageReviewScreen />}
      />
      <Route path="/gifts/:customerId" element={<GiftsReviewScreen />} />
      <Route path="/horses/:customerId" element={<HorsesReviewScreen />} />
      <Route
        path="/horses/:customerId/:horseId"
        element={<HorsesReviewScreen />}
      />
      <Route
        path="/relationships/:customerId"
        element={<RelationshipsReviewScreen />}
      />
      </Route>

      <Route path="/history/:customerId" element={<HistoryReviewScreen />} />
      <Route path="/letters" element={<LettersReviewScreen />} />
      <Route path="/letters/:customerId" element={<LettersReviewScreen />} />

      {/* Manager rails. Legacy /manager redirects to the approved Team
       * Live screen; both are wrapped in RequireRole so a sales_rep who
       * navigates directly sees a role denial instead of the screen. */}
      <Route
        path="/manager"
        element={<Navigate to="/team-live" replace />}
      />
      <Route
        path="/team-live"
        element={
          <RequireRole roles={['manager', 'executive']} rail="manager">
            <TeamLiveScreen />
          </RequireRole>
        }
      />
      {/* W37b (2026-10-20): the api-server admits both manager and
       * executive to GET /v1/executive/summary (MANAGER_ROLES in
       * routes/stubs-w3c.mjs, READ) and resolves 89608213 to `manager`,
       * so the UI gate matches the server instead of blocking Doug. */}
      <Route
        path="/executive-live"
        element={
          <RequireRole roles={['manager', 'executive']} rail="manager">
            <ExecutiveLiveScreen />
          </RequireRole>
        }
      />
      <Route path="/live-chat" element={<LiveChatScreen />} />
      <Route path="/live-chat/:customerId" element={<LiveChatScreen />} />
      <Route
        path="/customer-addresses/:customerId"
        element={<CustomerAddressesScreen />}
      />
      <Route
        path="/edit-preferences/:customerId"
        element={<EditPreferencesScreen />}
      />
      <Route path="/calls" element={<CallsCallbacksScreen />} />
      <Route path="/calls/:customerId" element={<CallsCallbacksScreen />} />
      <Route
        path="/follow-ups/:customerId"
        element={<SpecialFollowupsScreen />}
      />
      <Route path="/intake" element={<NewCustomerIntakeScreen />} />
      <Route path="/intake/:customerId" element={<NewCustomerIntakeScreen />} />
      <Route
        path="/incoming-email/:customerId"
        element={<IncomingEmailScreen />}
      />
      <Route
        path="/incoming-text/:customerId"
        element={<IncomingTextScreen />}
      />
      <Route path="/help" element={<WorkspaceChrome rail="help">
        <section className="card" style={{ padding: 24 }}>
          <h1>Help</h1><p>Workspace support is not connected in this design review.</p>
        </section>
      </WorkspaceChrome>} />
      {/* Legacy contact routes remain until their approved ports are integrated. */}
      <Route element={<Layout />}>
        <Route path="/my-credit" element={<MyCreditStub />} />
        <Route
          path="/coaching"
          element={
            <RequireRole roles={['manager', 'executive']} rail="manager">
              <CoachingStub />
            </RequireRole>
          }
        />
        <Route path="/contacts/:contactId" element={<ContactLayout />}>
          <Route index element={<Navigate to="current" replace />} />
          <Route path="current" element={<CurrentAction />} />
          <Route path="build-order" element={<BuildOrder />} />
          <Route path="horses" element={<Horses />} />
          <Route path="subscription" element={<Subscription />} />
          <Route path="gifts" element={<Gifts />} />
          <Route path="colic-coverage" element={<ColicCoverage />} />
          <Route path="relationships" element={<Relationships />} />
          <Route path="service-needs" element={<ServiceNeeds />} />
          <Route path="history" element={<LegacyHistoryRoute />} />
        </Route>
        <Route
          path="*"
          element={
            <div style={{ padding: 20 }}>
              Route not found. Open a contact record from HubSpot to load the
              workspace tabs.
            </div>
          }
        />
      </Route>
    </Routes>
    </ReviewStateProvider>
  );
}

function LegacyHistoryRoute(): ReactNode {
  const { contactId = '' } = useParams();
  const location = useLocation();
  const aliases: Record<string, string> = {
    'sample-contact-sarah-mitchell': 'contact_sarah_mitchell',
    'sample-contact-michael-thompson': 'contact_michael_thompson',
  };
  const customerId = aliases[contactId] ?? contactId;
  return <Navigate to={'/history/' + encodeURIComponent(customerId)} replace state={location.state} />;
}

// W23 (2026-10-20): live pending screen with elapsed-seconds timer and
// a manual reload button. The URL-bootstrap backup timer in
// hubspot-handshake.ts fires at 3s; if this screen is still visible at
// 5s, something deeper than throttled setTimeout is wrong and Doug can
// click Reload to try again. Elapsed counter runs client-side so Chrome
// DevTools can observe whether the setTimeout tick fires at all.
function PendingScreen(): ReactNode {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const start = Date.now();
    const id = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 500);
    return () => clearInterval(id);
  }, []);
  const search = typeof window !== 'undefined' && window.location ? window.location.search : '';
  const parentOk = typeof window !== 'undefined' && window.parent !== window;
  const stuck = elapsed >= 5;
  return (
    <main
      role="status"
      style={{
        padding: 24,
        fontFamily:
          'system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
      }}
    >
      <h1 style={{ marginTop: 0 }}>Opening Sales Workspace…</h1>
      <p>
        Waiting on the HubSpot session handshake. The URL-bootstrap fallback
        mounts the workspace automatically within ~3 seconds.
      </p>
      <p>
        <strong>Elapsed:</strong> {elapsed}s{stuck ? ' (handshake is unusually slow)' : ''}
      </p>
      {stuck && (
        <p>
          <button
            type="button"
            onClick={() => {
              try {
                window.location.reload();
              } catch {
                /* ignore */
              }
            }}
            style={{
              padding: '8px 16px',
              fontSize: 14,
              background: '#0366d6',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
            }}
          >
            Reload workspace
          </button>
        </p>
      )}
      <pre
        style={{
          background: '#f5f5f5',
          padding: 12,
          borderRadius: 6,
          fontSize: 12,
          overflow: 'auto',
        }}
      >
        handshake.status = pending{'\n'}
        elapsed = {elapsed}s{'\n'}
        iframe parent available = {String(parentOk)}{'\n'}
        location.search = {search || '(empty)'}
      </pre>
    </main>
  );
}

// Server-authorized next-task landing. The HubSpot session carries the
// current record id in context.objectId; that is the task the rep is
// looking at. Redirect into the approved Current Action review for it.
// Without a bound record (session.objectId missing) the landing shows a
// bounded message rather than guessing a task.
function NextTaskLanding(): ReactNode {
  const context = useHubSpotContext();
  if (context.status !== 'ready') return <main role="status" style={{ padding: 24 }}>Opening next task…</main>;
  const objectId = context.context.objectId;
  if (!objectId) {
    return (
      <WorkspaceChrome rail="my-work">
        <section className="card" style={{ padding: 24 }} aria-labelledby="next-task-empty">
          <span className="eyebrow">NO TASK IN SCOPE</span>
          <h1 id="next-task-empty">No task is in scope right now.</h1>
          <p>
            Open the Sales Workspace from a HubSpot contact record to pick
            up the next action for that customer.
          </p>
        </section>
      </WorkspaceChrome>
    );
  }
  return <Navigate to={`/current-action/${encodeURIComponent(objectId)}`} replace />;
}
